// Phase 37 · 23a — the board card, in the theme.
//
// Same anatomy as before (ticket number, channel, priority, SLA, subject,
// type, tags, timestamp, assignee) — nothing added, nothing dropped. What
// changed is that every colour is now a theme token, so a card reads the same
// way as a row on the Tickets register or a line in Backtrack.
//
// Priority hues. All four levels carry their own value so the level is
// readable without reading its label: green / neutral / terracotta / burgundy.
// Medium is deliberately NEUTRAL rather than the mock's steel-blue — in this
// theme slate is --co-machine and means "a machine did this" (embeddings,
// citations, automations). Painting the most common priority in a near-slate
// would make half the board look machine-generated, which is the one thing
// the machine semantic may never be spent on. Neutral is also the honest
// reading: medium is the default, so it says nothing in particular.
import { memo, useMemo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Mail, MessageSquare, Phone, Globe } from 'lucide-react';
import { calculateSlaStatus, type SlaStatus } from '@components/tickets/SlaIndicator';
import { initials, identityColor, personName } from '@components/tickets/identity';
import type { Ticket } from '@centraoneplus/shared-types';

const CHANNEL_ICONS: Record<string, React.ElementType> = {
  email: Mail,
  chat: MessageSquare,
  phone: Phone,
  web: Globe,
};

interface PriorityTheme {
  label: string;
  /** The left rail — the saturated value. */
  rail: string;
  /** Pill text. */
  ink: string;
  /** Pill hairline. */
  line: string;
  /** Pill fill. */
  wash: string;
  pulse: boolean;
}

const PRIORITY_THEME: Record<string, PriorityTheme> = {
  low: {
    label: 'Low',
    rail: 'var(--co-green)',
    ink: 'var(--co-green-ink)',
    line: 'var(--co-green-line)',
    wash: 'var(--co-green-wash)',
    pulse: false,
  },
  medium: {
    label: 'Medium',
    rail: 'var(--co-line-strong)',
    ink: 'var(--co-ink-3)',
    line: 'var(--co-line)',
    wash: 'var(--co-selected)',
    pulse: false,
  },
  high: {
    label: 'High',
    rail: 'var(--co-terracotta)',
    ink: 'var(--co-terracotta-ink)',
    line: 'var(--co-terracotta-line)',
    wash: 'var(--co-terracotta-wash)',
    pulse: false,
  },
  urgent: {
    label: 'Urgent',
    rail: 'var(--co-burgundy)',
    ink: 'var(--co-burgundy)',
    line: 'var(--co-burgundy-line)',
    wash: 'var(--co-burgundy-wash)',
    pulse: true,
  },
};

/** SLA is a deadline, not a priority: warn = terracotta, missed = burgundy. */
const SLA_TONE: Record<Exclude<SlaStatus, 'none'>, { color: string; label: string }> = {
  on_track: { color: 'var(--co-green)', label: 'On track' },
  at_risk: { color: 'var(--co-terracotta)', label: 'At risk' },
  breached: { color: 'var(--co-burgundy)', label: 'Breached' },
  met: { color: 'var(--co-green)', label: 'Met' },
};

function formatDate(date: string | Date) {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface TicketKanbanCardProps {
  ticket: Ticket;
  onClick: () => void;
  isDraggable?: boolean;
}

export const TicketKanbanCard = memo(function TicketKanbanCard({ ticket, onClick, isDraggable = true }: TicketKanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ticket.id,
    data: { ticket },
    disabled: !isDraggable,
  });

  const ChannelIcon = CHANNEL_ICONS[ticket.channel] || Mail;
  const priority = PRIORITY_THEME[ticket.priority] || PRIORITY_THEME.medium;
  const formattedDate = useMemo(() => formatDate(ticket.createdAt), [ticket.createdAt]);

  const sla = useMemo(() => {
    const { status } = calculateSlaStatus(
      ticket.resolutionDueAt ? new Date(ticket.resolutionDueAt) : null,
      ticket.resolvedAt ? new Date(ticket.resolvedAt) : null,
      30,
    );
    return status === 'none' ? null : SLA_TONE[status];
  }, [ticket.resolutionDueAt, ticket.resolvedAt]);

  const assignee = personName((ticket as any).assignee?.name) || undefined;

  // A DragOverlay renders the moving copy of the card while dragging, so the
  // source card must NOT also apply the drag translate. Otherwise two cards
  // follow the pointer — the overlay plus this translated original — and the
  // faint original drifts out behind the lanes as a duplicate/ghost on a wide
  // horizontal drag. Keep the source in place and just dim it as a drop
  // placeholder; the overlay handles the movement.
  const cardStyle = useMemo(() => {
    const base: React.CSSProperties = {
      borderRadius: 10,
      boxShadow: 'var(--co-shadow-card)',
    };
    if (isDragging) base.opacity = 0.4;
    return base;
  }, [isDragging]);

  return (
    <div
      ref={setNodeRef}
      style={cardStyle}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`
        group relative flex cursor-pointer select-none overflow-hidden
        border bg-[var(--co-raised)]
        transition-colors duration-150
        ${isDragging
          ? 'border-[var(--co-brass)]'
          : 'border-[var(--co-line)] hover:border-[var(--co-brass)]'
        }
      `}
    >
      {/* Priority rail */}
      <span
        aria-hidden
        className="w-[3px] flex-none transition-[width] duration-150 group-hover:w-[4px]"
        style={{ background: priority.rail }}
      />

      <div className="min-w-0 flex-1 px-[13px] py-[11px]">
        {/* Top row: channel + ticket number — priority pill — SLA dot */}
        <div className="flex items-center gap-[7px]">
          <ChannelIcon className="h-3 w-3 flex-none text-[var(--co-ink-4)]" aria-hidden />
          <span className="co-mono min-w-0 flex-1 truncate text-[10.5px] font-medium tracking-[.04em] text-[var(--co-ink-3)]">
            {ticket.ticketNumber || `#${ticket.id.slice(0, 7)}`}
          </span>
          <span
            className="co-mono inline-flex flex-none items-center gap-[5px] rounded-[5px] border px-[7px] py-[2px] text-[8.5px] font-semibold uppercase leading-none tracking-[.1em]"
            style={{ background: priority.wash, borderColor: priority.line, color: priority.ink }}
            title={`Priority: ${priority.label}`}
          >
            {priority.pulse && (
              <span
                aria-hidden
                className="h-[5px] w-[5px] flex-none animate-pulse rounded-full"
                style={{ background: priority.ink }}
              />
            )}
            {priority.label}
          </span>
          {sla && (
            <span
              className="h-2 w-2 flex-none rounded-full"
              style={{ background: sla.color }}
              title={`SLA: ${sla.label}`}
            />
          )}
        </div>

        {/* Subject */}
        <h4 className="mt-[9px] line-clamp-2 text-[13.5px] font-semibold leading-[1.35] text-[var(--co-ink)]">
          {ticket.subject}
        </h4>

        {/* Ticket Type — the dot keeps the tenant's configured type colour,
            which is data, not palette. */}
        {ticket.ticketType && (
          <div className="mt-[7px] flex items-center gap-[6px]">
            <span
              className="h-[6px] w-[6px] flex-none rounded-full"
              style={{ backgroundColor: ticket.ticketType.color || 'var(--co-ink-4)' }}
            />
            <span className="truncate text-[11.5px] text-[var(--co-ink-2)]">
              {ticket.ticketType.name}
            </span>
          </div>
        )}

        {/* Tags — neutral by design. A tag is free text somebody typed; it
            carries no status, so it may not borrow a status hue. */}
        {ticket.tags && ticket.tags.length > 0 && (
          <div className="mt-[9px] flex flex-wrap gap-[5px]">
            {ticket.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="co-mono inline-flex items-center rounded-[5px] border border-[var(--co-line)] bg-[var(--co-selected)] px-[7px] py-[3px] text-[9px] font-semibold leading-none tracking-[.06em] text-[var(--co-ink-3)]"
              >
                {tag}
              </span>
            ))}
            {ticket.tags.length > 2 && (
              <span className="co-mono self-center text-[9px] font-semibold text-[var(--co-ink-4)]">
                +{ticket.tags.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Footer: timestamp + assignee */}
        <div className="mt-[11px] flex items-center gap-2 border-t border-[var(--co-line-subtle)] pt-[9px]">
          <span className="co-mono flex-1 text-[10.5px] tabular-nums text-[var(--co-ink-3)]">
            {formattedDate}
          </span>

          {ticket.assigneeId ? (
            <span
              role="img"
              aria-label={`Assigned to ${assignee || 'a teammate'}`}
              title={`Assigned to ${assignee || 'a teammate'}`}
              className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[7px] text-[9px] font-semibold text-[var(--co-ink-invert)]"
              style={{ background: identityColor(assignee) }}
            >
              {initials(assignee)}
            </span>
          ) : (
            /* Unowned work should look unfinished — a dashed slot, not an
               empty circle that reads as a loading state. */
            <span
              role="img"
              aria-label="Unassigned"
              title="Unassigned"
              className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[6px] border border-dashed border-[var(--co-line-strong)] text-[10px] leading-none text-[var(--co-ink-4)]"
            >
              ?
            </span>
          )}
        </div>
      </div>
    </div>
  );
});
